"use client";

import React, { useState, useEffect } from "react";
import { useCart } from "../context/CartContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaShoppingCart,
  FaTimes,
  FaTrash,
  FaPlus,
  FaMinus,
} from "react-icons/fa";
import { loadStripe } from "@stripe/stripe-js";

const stripePromise = loadStripe(
  process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!
);

export default function CartComponent() {
  const { cart, removeFromCart, updateQuantity, clearCart } = useCart();
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = cart.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  // Prevent body scroll when cart is open
  useEffect(() => {
    if (isCartOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isCartOpen]);

  const handleCheckout = async () => {
    setIsLoading(true);
    try {
      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: cart }),
      });

      if (!response.ok) throw new Error("Failed to create checkout session");

      const { sessionId } = await response.json();
      const stripe = await stripePromise;

      if (!stripe) throw new Error("Stripe failed to load");

      await stripe.redirectToCheckout({ sessionId });
    } catch (error) {
      console.error("Checkout error:", error);
      alert("An error occurred during checkout. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const cartButtonVariants = {
    hover: { scale: 1.05 },
    tap: { scale: 0.95 },
  };

  const cartModalVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const cartContentVariants = {
    hidden: { x: "100%", opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 200,
      },
    },
    exit: {
      x: "100%",
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  return (
    <>
      {/* Cart Button */}
      <motion.button
        onClick={() => setIsCartOpen(true)}
        className="relative text-white hover:text-gray-300 transition-colors duration-300 flex items-center space-x-2"
        variants={cartButtonVariants}
        whileHover="hover"
        whileTap="tap"
      >
        <FaShoppingCart className="text-xl" />
        {totalItems > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-2 -right-2 bg-white text-black text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold"
          >
            {totalItems}
          </motion.span>
        )}
      </motion.button>

      {/* Cart Modal */}
      <AnimatePresence>
        {isCartOpen && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-75 z-50 flex justify-end"
            variants={cartModalVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            onClick={() => setIsCartOpen(false)}
          >
            <motion.div
              className="bg-black border-l border-white w-full max-w-md h-full overflow-hidden flex flex-col"
              variants={cartContentVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Cart Header */}
              <div className="border-b border-white p-6 flex justify-between items-center">
                <h2 className="text-2xl text-white uppercase tracking-widest font-goodfont">
                  Shopping Cart
                </h2>
                <motion.button
                  onClick={() => setIsCartOpen(false)}
                  className="text-white hover:text-gray-300 transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FaTimes className="text-xl" />
                </motion.button>
              </div>

              {/* Cart Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {cart.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center text-gray-400 mt-8"
                  >
                    <FaShoppingCart className="text-6xl mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-goodfont">Your cart is empty</p>
                    <p className="text-sm mt-2 font-goodfont">
                      Add some music to get started!
                    </p>
                  </motion.div>
                ) : (
                  <div className="space-y-4">
                    <AnimatePresence>
                      {cart.map((item, index) => (
                        <motion.div
                          key={item.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, x: -100 }}
                          transition={{ delay: index * 0.1 }}
                          className="border border-white p-4 flex items-center space-x-4 hover:border-gray-400 transition-colors"
                        >
                          {/* Item Details */}
                          <div className="flex-1">
                            <h3 className="text-white font-medium font-goodfont uppercase tracking-wider">
                              {item.name}
                            </h3>
                            <p className="text-gray-400 text-sm font-goodfont">
                              ${item.price.toFixed(2)} each
                            </p>

                            {/* Quantity Controls */}
                            <div className="flex items-center space-x-2 mt-2">
                              <motion.button
                                onClick={() =>
                                  updateQuantity(item.id, item.quantity - 1)
                                }
                                className="text-white border border-white w-6 h-6 flex items-center justify-center hover:bg-white hover:text-black transition-colors"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                              >
                                <FaMinus className="text-xs" />
                              </motion.button>
                              <span className="text-white w-8 text-center font-goodfont">
                                {item.quantity}
                              </span>
                              <motion.button
                                onClick={() =>
                                  updateQuantity(item.id, item.quantity + 1)
                                }
                                className="text-white border border-white w-6 h-6 flex items-center justify-center hover:bg-white hover:text-black transition-colors"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                              >
                                <FaPlus className="text-xs" />
                              </motion.button>
                            </div>
                          </div>

                          {/* Item Total & Remove */}
                          <div className="text-right">
                            <p className="text-white font-medium font-goodfont">
                              ${(item.price * item.quantity).toFixed(2)}
                            </p>
                            <motion.button
                              onClick={() => removeFromCart(item.id)}
                              className="text-gray-400 hover:text-red-400 transition-colors mt-2"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <FaTrash className="text-sm" />
                            </motion.button>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                )}
              </div>

              {/* Cart Footer */}
              {cart.length > 0 && (
                <div className="border-t border-white p-6 space-y-4">
                  <div className="flex justify-between items-center text-white">
                    <span className="text-xl uppercase tracking-wider font-goodfont">
                      Total:
                    </span>
                    <span className="text-2xl font-bold font-goodfont">
                      ${totalPrice.toFixed(2)}
                    </span>
                  </div>

                  <motion.button
                    onClick={handleCheckout}
                    disabled={isLoading}
                    className="w-full bg-white text-black py-3 uppercase tracking-widest font-goodfont hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    whileHover={{ scale: isLoading ? 1 : 1.02 }}
                    whileTap={{ scale: isLoading ? 1 : 0.98 }}
                  >
                    {isLoading ? (
                      <motion.div
                        className="inline-block w-5 h-5 border-2 border-black border-t-transparent rounded-full"
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                      />
                    ) : (
                      "Proceed to Checkout"
                    )}
                  </motion.button>

                  <motion.button
                    onClick={clearCart}
                    className="w-full border border-white text-white py-2 uppercase tracking-wider font-goodfont hover:bg-white hover:text-black transition-colors text-sm"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Clear Cart
                  </motion.button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
